// 问卷选项标签常量
// 用于在各个组件中统一显示选项标签

import {
  EnterpriseType,
  RegistrationType,
  CreditRating,
  InternationalRating,
  AssetScale,
  Industry,
  DebtRatio,
  ProfitScale,
  CashFlowStatus,
  DefaultRecord,
  GreenProjectStatus,
  GreenProjectCategory,
  GreenStandardCompliance,
  InvestmentScale,
  IssuanceScale,
  IssuanceTime,
  ProductType,
  TargetMarket,
  LitigationStatus,
  EnvironmentalCompliance,
  RegulatoryApproval,
  UrgencyLevel,
} from '../types/questionnaire';

// 企业性质标签
export const ENTERPRISE_TYPE_LABELS = {
  [EnterpriseType.CENTRAL_SOE]: '中央企业',
  [EnterpriseType.LOCAL_SOE]: '地方国有企业',
  [EnterpriseType.LISTED_COMPANY]: '上市公司',
  [EnterpriseType.PRIVATE_ENTERPRISE]: '民营企业',
  [EnterpriseType.FOREIGN_ENTERPRISE]: '外资企业',
  [EnterpriseType.OTHER]: '其他',
};

// 注册地标签
export const REGISTRATION_TYPE_LABELS: Record<string, string> = {
  [RegistrationType.MAINLAND_CHINA]: '中国境内',
  [RegistrationType.HONG_KONG]: '香港',
  [RegistrationType.MACAU]: '澳门',
  [RegistrationType.OTHER_OVERSEAS]: '其他境外地区',
  // 兼容旧数据格式
  overseas: '中国境外（含港澳台）',
};

// 信用评级标签
export const CREDIT_RATING_LABELS = {
  [CreditRating.AAA]: 'AAA',
  [CreditRating.AA_PLUS]: 'AA+',
  [CreditRating.AA]: 'AA',
  [CreditRating.AA_MINUS]: 'AA-',
  [CreditRating.A_PLUS_AND_BELOW]: 'A+及以下',
  [CreditRating.NO_RATING]: '暂无评级',
};

// 国际评级标签
export const INTERNATIONAL_RATING_LABELS = {
  [InternationalRating.INVESTMENT_GRADE]: '投资级',
  [InternationalRating.NON_INVESTMENT_GRADE]: '非投资级',
  [InternationalRating.NO_INTERNATIONAL_RATING]: '暂无国际评级',
};

// 资产规模标签
export const ASSET_SCALE_LABELS = {
  [AssetScale.ABOVE_100B]: '1000亿元以上',
  [AssetScale.FROM_50B_TO_100B]: '500-1000亿元',
  [AssetScale.FROM_20B_TO_50B]: '200-500亿元',
  [AssetScale.FROM_10B_TO_20B]: '100-200亿元',
  [AssetScale.FROM_5B_TO_10B]: '50-100亿元',
  [AssetScale.BELOW_5B]: '50亿元以下',
};

// 行业标签
export const INDUSTRY_LABELS = {
  [Industry.ENERGY]: '能源',
  [Industry.INFRASTRUCTURE]: '基础设施',
  [Industry.FINANCIAL_SERVICES]: '金融服务',
  [Industry.REAL_ESTATE]: '房地产',
  [Industry.MANUFACTURING]: '制造业',
  [Industry.CONSUMER_SERVICES]: '消费服务',
  [Industry.TECHNOLOGY]: '科技',
  [Industry.OTHER]: '其他',
};

// 资产负债率标签
export const DEBT_RATIO_LABELS = {
  [DebtRatio.BELOW_60]: '60%以下',
  [DebtRatio.FROM_60_TO_70]: '60%-70%',
  [DebtRatio.FROM_70_TO_80]: '70%-80%',
  [DebtRatio.ABOVE_80]: '80%以上',
  [DebtRatio.UNKNOWN]: '不清楚',
};

// 净利润规模标签
export const PROFIT_SCALE_LABELS = {
  [ProfitScale.ABOVE_1B]: '10亿元以上',
  [ProfitScale.FROM_500M_TO_1B]: '5-10亿元',
  [ProfitScale.FROM_100M_TO_500M]: '1-5亿元',
  [ProfitScale.FROM_0_TO_100M]: '0-1亿元',
  [ProfitScale.LOSS]: '亏损',
};

// 现金流状况标签
export const CASH_FLOW_STATUS_LABELS = {
  [CashFlowStatus.EXCELLENT]: '优秀',
  [CashFlowStatus.GOOD]: '良好',
  [CashFlowStatus.FAIR]: '一般',
  [CashFlowStatus.POOR]: '较差',
  [CashFlowStatus.UNKNOWN]: '不清楚',
};

// 违约记录标签
export const DEFAULT_RECORD_LABELS = {
  [DefaultRecord.NO_DEFAULT]: '无任何违约记录',
  [DefaultRecord.MINOR_RESOLVED]: '轻微逾期已解决',
  [DefaultRecord.DEFAULT_RESTRUCTURED]: '违约已重组',
  [DefaultRecord.CURRENT_DEFAULT]: '目前存在违约',
};

// 绿色项目状态标签
export const GREEN_PROJECT_STATUS_LABELS = {
  [GreenProjectStatus.CLEAR_PROJECT]: '有明确项目',
  [GreenProjectStatus.IN_PLANNING]: '规划中',
  [GreenProjectStatus.NO_CLEAR_PROJECT]: '无明确项目',
};

// 绿色项目类别标签
export const GREEN_PROJECT_CATEGORY_LABELS = {
  [GreenProjectCategory.RENEWABLE_ENERGY]: '可再生能源',
  [GreenProjectCategory.ENERGY_SAVING]: '节能减排',
  [GreenProjectCategory.ENVIRONMENTAL_TREATMENT]: '环境治理',
  [GreenProjectCategory.CLEAN_TRANSPORTATION]: '清洁交通',
  [GreenProjectCategory.GREEN_BUILDING]: '绿色建筑',
  [GreenProjectCategory.CIRCULAR_ECONOMY]: '循环经济',
  [GreenProjectCategory.ECOLOGICAL_PROTECTION]: '生态保护',
  [GreenProjectCategory.OTHER_GREEN]: '其他绿色项目',
};

// 绿色标准符合性标签
export const GREEN_STANDARD_COMPLIANCE_LABELS = {
  [GreenStandardCompliance.CERTIFIED]: '已获得权威机构绿色认证',
  [GreenStandardCompliance.PBOC_COMPLIANT]: '符合央行绿色金融目录',
  [GreenStandardCompliance.INTERNATIONAL_COMPLIANT]: '符合国际绿色标准',
  [GreenStandardCompliance.BASICALLY_COMPLIANT]: '基本符合需确认',
  [GreenStandardCompliance.UNCERTAIN]: '不确定是否符合',
};

// 投资规模标签
export const INVESTMENT_SCALE_LABELS = {
  [InvestmentScale.ABOVE_10B]: '100亿元以上',
  [InvestmentScale.FROM_5B_TO_10B]: '50-100亿元',
  [InvestmentScale.FROM_2B_TO_5B]: '20-50亿元',
  [InvestmentScale.FROM_1B_TO_2B]: '10-20亿元',
  [InvestmentScale.FROM_500M_TO_1B]: '5-10亿元',
  [InvestmentScale.BELOW_500M]: '5亿元以下',
};

// 发行规模标签
export const ISSUANCE_SCALE_LABELS = {
  [IssuanceScale.ABOVE_5B]: '50亿元以上',
  [IssuanceScale.FROM_2B_TO_5B]: '20-50亿元',
  [IssuanceScale.FROM_1B_TO_2B]: '10-20亿元',
  [IssuanceScale.FROM_500M_TO_1B]: '5-10亿元',
  [IssuanceScale.FROM_300M_TO_500M]: '3-5亿元',
  [IssuanceScale.BELOW_300M]: '3亿元以下',
};

// 发行时间标签
export const ISSUANCE_TIME_LABELS = {
  [IssuanceTime.WITHIN_1_MONTH]: '1个月内',
  [IssuanceTime.WITHIN_3_MONTHS]: '3个月内',
  [IssuanceTime.WITHIN_6_MONTHS]: '6个月内',
  [IssuanceTime.WITHIN_1_YEAR]: '1年内',
  [IssuanceTime.ABOVE_1_YEAR]: '1年以上',
};

// 产品类型标签
export const PRODUCT_TYPE_LABELS = {
  [ProductType.PUBLIC_GREEN_BOND]: '公开发行绿色公司债',
  [ProductType.PRIVATE_GREEN_BOND]: '私募绿色债券',
  [ProductType.GREEN_MTN]: '绿色中期票据',
  [ProductType.GREEN_SYNDICATED_LOAN]: '绿色银团贷款',
  [ProductType.GREEN_ABS_REITS]: '绿色ABS/REITs',
  [ProductType.OTHER_GREEN_PRODUCT]: '其他绿色融资产品',
};

// 目标市场标签
export const TARGET_MARKET_LABELS = {
  [TargetMarket.DOMESTIC]: '境内市场（银行间/交易所）',
  [TargetMarket.OFFSHORE]: '离岸市场（香港/新加坡等）',
  [TargetMarket.MACAU]: '澳门市场（MOX）',
  [TargetMarket.FLEXIBLE]: '根据条件灵活选择',
};

// 诉讼状况标签
export const LITIGATION_STATUS_LABELS = {
  [LitigationStatus.NO_MAJOR_LITIGATION]: '无重大诉讼',
  [LitigationStatus.MINOR_LITIGATION]: '有诉讼但金额较小',
  [LitigationStatus.MAJOR_LITIGATION_HIGH_WIN_RATE]: '有重大诉讼但胜诉概率高',
  [LitigationStatus.MAJOR_LITIGATION_HIGH_LOSS_RISK]: '存在重大败诉风险',
};

// 环保合规标签
export const ENVIRONMENTAL_COMPLIANCE_LABELS = {
  [EnvironmentalCompliance.GOOD_COMPLIANCE]: '环保合规良好，无违规记录',
  [EnvironmentalCompliance.MINOR_ISSUES_RESOLVED]: '有轻微环保问题但已整改',
  [EnvironmentalCompliance.VIOLATIONS_BEING_HANDLED]: '存在环保违规正在处理',
  [EnvironmentalCompliance.MAJOR_VIOLATION_RISK]: '有重大环保违规风险',
};

// 监管审批标签
export const REGULATORY_APPROVAL_LABELS = {
  [RegulatoryApproval.ALL_APPROVALS_OBTAINED]: '已获得所有必要批文',
  [RegulatoryApproval.MOST_APPROVALS_OBTAINED]: '大部分批文已获得',
  [RegulatoryApproval.APPLYING_KEY_APPROVALS]: '正在申请关键批文',
  [RegulatoryApproval.NOT_STARTED]: '尚未开始审批流程',
};

// 紧急程度标签
export const URGENCY_LEVEL_LABELS = {
  [UrgencyLevel.VERY_URGENT]: '非常紧急（希望24小时内联系）',
  [UrgencyLevel.URGENT]: '比较紧急（希望3天内联系）',
  [UrgencyLevel.NORMAL]: '一般紧急（希望1周内联系）',
  [UrgencyLevel.NOT_URGENT]: '不急（有合适机会再联系）',
};

// 风险等级颜色映射
export const RISK_LEVEL_COLORS = {
  low: 'green',
  medium: 'orange',
  high: 'red',
};

// 风险等级标签
export const RISK_LEVEL_LABELS = {
  low: '低风险',
  medium: '中等风险',
  high: '高风险',
};

// 问卷状态颜色映射
export const STATUS_COLORS = {
  draft: 'default',
  submitted: 'processing',
  under_review: 'warning',
  approved: 'success',
  rejected: 'error',
  need_supplement: 'orange',
};

// 筛选决策颜色映射
export const DECISION_COLORS = {
  auto_pass: 'green',
  priority_review: 'blue',
  conditional_pass: 'orange',
  not_pass: 'red',
};

// 筛选决策标签
export const DECISION_LABELS = {
  auto_pass: '自动通过',
  priority_review: '重点关注',
  conditional_pass: '需要补强',
  not_pass: '暂不通过',
};
